// Which file sources a preset load may put on stage: a synchronous mirror of the library, so `loadPreset` never awaits (sources-v2 D10).
import { IS_DEV } from '~/lib/constants'
import { fileSourceId, isFileSource, libraryIdOf } from './ids'
import library from './library'

const DURATION_SLACK_S = 0.1

/** What a preset saves beside `file:<id>`, so another machine finds its own copy. Unknown fields are left out. */
export const fingerprintOf = ({ name, size, duration }) =>
	Object.fromEntries(Object.entries({ name, size, duration }).filter(([, v]) => v != null))

const sameFile = (fp, e) =>
	e.name === fp.name &&
	e.size === fp.size &&
	(fp.duration == null || e.duration == null || Math.abs(fp.duration - e.duration) < DURATION_SLACK_S)

/** The local source a saved `file:<id>` resolves to: its own entry, else an entry matching the fingerprint; only an entry that opens without a gesture. Null: the input stays. */
export const resolveFile = (id, fingerprint, entries) => {
	const own = entries.find(e => e.id === libraryIdOf(id))
	if (own?.openable) return fileSourceId(own.id)
	if (!fingerprint?.name || fingerprint.size == null) return null
	const match = entries.find(e => e.openable && sameFile(fingerprint, e))
	return match ? fileSourceId(match.id) : null
}

let entries = []
let folders = []
const listeners = new Set()
// Each read of the library makes new thumb Blobs: one object URL per entry, replaced only when its thumb changes.
const thumbs = new Map()

const thumbUrlOf = e => {
	if (!(e.thumb instanceof Blob)) return null
	const had = thumbs.get(e.id)
	if (had?.size === e.thumb.size) return had.url
	if (had) URL.revokeObjectURL(had.url)
	const url = URL.createObjectURL(e.thumb)
	thumbs.set(e.id, { size: e.thumb.size, url })
	return url
}

const dropStaleThumbs = ids => {
	for (const [id, { url }] of thumbs)
		if (!ids.has(id)) {
			URL.revokeObjectURL(url)
			thumbs.delete(id)
		}
}
let started = false
let refreshing = false
let dirty = false

// A change landing mid-refresh runs one more, never a parallel one.
const refresh = () => {
	if (refreshing) {
		dirty = true
		return
	}
	refreshing = true
	Promise.all([library.list(), library.openableIds(), library.folders()])
		.then(([list, openable, linked]) => {
			entries = list.map(e => ({ ...e, openable: openable.has(e.id), thumbUrl: thumbUrlOf(e) }))
			folders = linked
			dropStaleThumbs(new Set(list.map(e => e.id)))
			for (const fn of listeners) fn()
		})
		.catch(e => console.scope('files').warn('catalog refresh failed', e))
		.finally(() => {
			refreshing = false
			if (dirty) {
				dirty = false
				refresh()
			}
		})
}

/** Mirrors the library from now on; a grant given in a click lands through the library's own notify. */
export const startFileCatalog = () => {
	if (started) return
	started = true
	library.subscribe(refresh)
	refresh()
}

/** Library entries with `openable` and `thumbUrl`, as of the last refresh. */
export const fileEntries = () => entries

/** Linked folders with `granted`, as of the last refresh. */
export const linkedFolders = () => folders

/** The local file source a file fill's `source` points at, by its bag's fingerprint; null for a non-file or no local copy. */
export const resolveSavedFile = (source, bag, entries = fileEntries()) =>
	isFileSource(source) ? resolveFile(source, bag?.file, entries) : null

/** @returns {() => void} unsubscribe */
export const onFileCatalog = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

if (IS_DEV) window.fileCatalog = fileEntries
